/**
 * ReminderStore — F 490's app-side CRUD for reminders and notifications.
 *
 * WHY A STORE AND NOT DIRECT ADAPTER CALLS
 * ----------------------------------------
 * Same reason `TaskStore` exists (see its header): the storage adapter is
 * generic — any key to any store — while a store enforces the SHAPE of what
 * it writes and gives the rest of the app one place to ask "what reminders
 * exist?" and "what is unread?".
 *
 * WHY THE KEYS ARE `reminder:` AND `notification:`
 * -----------------------------------------------
 * The keys carry the `freshcards:` prefix EXPLICITLY, because
 * `RemoteState.set` stores a key VERBATIM and `isSyncable` accepts only a
 * prefixed key. So `freshcards:reminder:<id>` is the key, and it is the
 * EXACT prefix the server-side scheduler reads:
 * `/opt/fcc-notify/notify-cron.mjs` scans `freshcards:reminder:` and writes
 * `freshcards:notification:`. The two halves agree on one namespace
 * because both were derived from the same measured rule, not because they
 * were agreed by hand.
 *
 * THE SCHEDULER IS NOT IN THIS PROCESS
 * ------------------------------------
 * The scheduler runs on the server, on cron. This store only ever writes
 * REMINDERS. The only thing that writes a notification is the scheduler —
 * so an app-side "create notification" would be a second writer racing the
 * real one. There is deliberately no `createNotification` here.
 */

import type { Notification, Reminder, ReminderRepeat, ReminderState } from '../../types/models/Reminder';
import {
  computeNextFireAt,
  isNotification,
  isReminder,
  reminderFcid,
} from '../../types/models/Reminder';
import { EventBus } from '@core/event-bus';
import { remoteAdapter } from '../storage/RemoteState';
// F 490 — the SERVER read. The notification the scheduler wrote lives on
// the server, and the app's cache is only populated by `init()` — so a
// cache read can never see a notification the scheduler created. See
// `readNotificationsFromGraph` below for the full story.
import { graphFetch, graphAuthProblem } from '../storage/graphAuth';

/**
 * WHY `remoteAdapter` AND NOT `storage.getDefaultAdapter()`
 * ---------------------------------------------------
 * THIS IS THE SEAM THAT DECIDES WHETHER THE FEATURE WORKS AT ALL, and it
 * was wrong in the first cut.
 *
 * MEASURED on production: the reminder was saved, the popover said "Saved",
 * the browser's localStorage held the node — and the SERVER-SIDE SCHEDULER
 * reported "nothing due (0 reminder node(s) scanned)". The reminder was in
 * the browser and nowhere else, because `storage.getDefaultAdapter()` is
 * the LOCALSTORAGE adapter. A browser-local reminder cannot fire when the
 * browser is closed, which is the entire premise of the feature.
 *
 * `remoteAdapter` (from `src/core/storage/RemoteState.ts`) is the
 * GRAPH-BACKED view over the same cache:
 *
 *   - `save` writes into the in-memory cache, which is flushed to the FCC
 *     state service — so the SERVER sees the reminder, and the cron
 *     scheduler can act on it;
 *   - `list` unions the cache with the localStorage mirror, so a reminder
 *     written before a reload is still found;
 *   - `delete` removes it.
 *
 * The `TaskStore` has the same shape (it uses `getDefaultAdapter()`), which
 * is why tasks are also browser-local. That is a pre-existing property of
 * the task feature and NOT this dispatch's scope to change — but the
 * reminder MUST be graph-backed, because a server-side scheduler is the
 * other half of this feature.
 *
 * The KEY NAMESPACE. It must carry the `freshcards:` prefix EXPLICITLY.
 *
 * MEASURED: `RemoteState.set(key, value)` stores the key VERBATIM — it
 * does not add a prefix — and `isSyncable(key)` accepts a key only if it
 * starts with `freshcards:` (or is in a short legacy list). So a key of
 * `reminder:<id>` would be neither cached nor synced: it would be
 * invisible to the graph, invisible to the scheduler, and rejected by
 * `isSyncable` before it ever reached the server.
 *
 * The app's own graph writes do the same thing: `pageStorage.ts` uses
 * `freshcards:pages`, `freshcards:currentPageId`, and `entityHistory.ts`
 * builds `freshcards:fc_entity_history:…`.
 *
 * ⇒ `freshcards:reminder:<id>` and `freshcards:notification:<id>`, which
 * are the EXACT prefixes `/opt/fcc-notify/notify-cron.mjs` scans and
 * writes.
 */
const REMINDER_PREFIX = 'freshcards:reminder:';
const NOTIFICATION_PREFIX = 'freshcards:notification:';

export interface ReminderStoreLike {
  save(key: string, value: unknown): Promise<void>;
  load(key: string): Promise<unknown>;
  delete(key: string): Promise<void>;
  list(prefix: string): Promise<string[]>;
}

export type ReminderEvent =
  | { type: 'reminder:created'; reminder: Reminder }
  | { type: 'reminder:updated'; reminder: Reminder }
  | { type: 'reminder:deleted'; reminderId: string }
  | { type: 'notification:read'; notification: Notification };

export type ReminderEventHandler = (event: ReminderEvent) => void;

/** The minimum bus this store needs. */
interface MinimalBus {
  emit(event: string, data: any): void;
  on(event: string, handler: (data: any) => void): () => void;
}

function createFallbackBus(): MinimalBus {
  const handlers = new Map<string, Set<(data: any) => void>>();
  return {
    emit(event, data) {
      (handlers.get(event) || []).forEach((h) => {
        try { h(data); } catch (e) { console.error('[ReminderStore] handler threw', e); }
      });
    },
    on(event, handler) {
      if (!handlers.has(event)) handlers.set(event, new Set());
      handlers.get(event)!.add(handler);
      return () => { handlers.get(event)?.delete(handler); };
    },
  };
}

function wrapHostBus(bus: EventBus): MinimalBus {
  return {
    emit(event, data) { (bus as any).emit(event, data); },
    on(event, handler) {
      const wrapped = (data: any) => handler(data);
      (bus as any).on(event, wrapped);
      return () => { try { (bus as any).off?.(event, wrapped); } catch { /* no off() */ } };
    },
  };
}

function defaultAdapter(): ReminderStoreLike {
  // The GRAPH-BACKED adapter. See the note above: the default adapter is
  // localStorage, and a browser-local reminder is a reminder that cannot
  // fire.
  return remoteAdapter as unknown as ReminderStoreLike;
}

export class ReminderStore {
  private adapter: ReminderStoreLike;
  private bus: MinimalBus;
  private hostHandlers: Map<ReminderEventHandler, { event: string; wrapped: (data: any) => void }> = new Map();

  constructor(adapter?: ReminderStoreLike, eventBus?: EventBus) {
    this.adapter = adapter ?? defaultAdapter();
    this.bus = eventBus ? wrapHostBus(eventBus) : createFallbackBus();
  }

  /**
   * Subscribe to reminder events. Returns an unsubscribe function.
   */
  subscribe(handler: ReminderEventHandler): () => void {
    const events: ReminderEvent['type'][] = [
      'reminder:created', 'reminder:updated', 'reminder:deleted', 'notification:read',
    ];
    const unsubs = events.map((e) => this.bus.on(e, handler));
    return () => { unsubs.forEach((u) => u()); };
  }

  // ── reminders ──────────────────────────────────────────────────────

  /** Save (create or update) a reminder. Validates the shape. */
  async save(reminder: Reminder): Promise<Reminder> {
    if (!isReminder(reminder)) {
      throw new Error(`ReminderStore.save: invalid reminder shape (id=${(reminder as Reminder)?.id})`);
    }
    const now = new Date().toISOString();
    const toPersist: Reminder = { ...reminder, updatedAt: now };
    const key = `${REMINDER_PREFIX}${reminder.id}`;
    // F 490 — DIAGNOSTIC. Remove before the report is finalised.
    await this.adapter.save(key, toPersist);
    this.bus.emit('reminder:updated', { type: 'reminder:updated', reminder: toPersist });
    return toPersist;
  }
  /**
   * Create a reminder for a date, computing `next_fire_at`.
   *
   * This is the one place a reminder is born, and it computes the next
   * firing rather than accepting it: a reminder whose `next_fire_at` was
   * unset would be INVISIBLE to the scheduler, which scans for
   * `next_fire_at <= now`.
   */
  async create(args: {
    id: string;
    parentNodeId: string;
    scheduledAt: string;
    repeat?: ReminderRepeat;
    repeatOn?: string | null;
    repeatTime?: string | null;
    until?: string | null;
    datePropertyId?: string | null;
    text?: string | null;
  }): Promise<Reminder> {
    const now = new Date();
    const repeat: ReminderRepeat = args.repeat ?? 'none';
    const scheduled = new Date(args.scheduledAt);
    if (Number.isNaN(scheduled.getTime())) {
      throw new Error(`ReminderStore.create: scheduledAt is not a valid date: ${args.scheduledAt}`);
    }
    const reminder: Reminder = {
      id: args.id,
      fcid: reminderFcid(args.id),
      scheduled_at: args.scheduledAt,
      repeat,
      repeat_on: args.repeatOn ?? null,
      repeat_time: args.repeatTime ?? null,
      // THE FIRST FIRING, NOT THE SECOND.
      //
      // MEASURED on production: a daily reminder set for 01:22 was written
      // with `next_fire_at` at 01:22 TOMORROW, so the scheduler found
      // nothing due and the reminder never rang — every recurring reminder
      // skipped its first firing. The evidence was in the record itself:
      // its `updatedAt` was still the CREATION time, so the scheduler had
      // never touched it. There was nothing for it to fire.
      //
      // `computeNextFireAt` computes the occurrence AFTER a firing, so
      // seeding a brand-new reminder with it skips one. The first firing
      // is `scheduled_at`; the scheduler advances from there.
      next_fire_at: scheduled.toISOString(),
      until: args.until ?? null,
      parent_node_id: args.parentNodeId,
      date_property_id: args.datePropertyId ?? null,
      state: 'scheduled',
      text: args.text ?? null,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
    const saved = await this.save(reminder);
    this.bus.emit('reminder:created', { type: 'reminder:created', reminder: saved });
    return saved;
  }

  /** Load one reminder. Returns null when missing or malformed. */
  async load(id: string): Promise<Reminder | null> {
    const raw = await this.adapter.load(`${REMINDER_PREFIX}${id}`);
    if (!raw) return null;
    return isReminder(raw) ? raw : null;
  }

  /** List every reminder. Malformed records are skipped, not thrown on. */
  async list(): Promise<Reminder[]> {
    const keys = await this.adapter.list(REMINDER_PREFIX);
    const out: Reminder[] = [];
    for (const k of keys) {
      const raw = await this.adapter.load(k);
      if (isReminder(raw)) out.push(raw);
    }
    return out;
  }

  /**
   * The reminders that are still waiting to fire.
   *
   * `state === 'scheduled'` is the filter the SCHEDULER uses, and this
   * method deliberately does NOT use it. A reminder that has FIRED keeps
   * its place in the series with `state === 'fired'`; a reminder that was
   * SKIPPED has already advanced to its next occurrence. Both are alive —
   * they are still in a series that will fire again.
   *
   * MEASURED, and this was a real bug: the original filter was
   * `scheduled || fired`, so a SKIPPED reminder dropped out of the active
   * list. The user would skip one occurrence, and the reminder would then
   * vanish from the UI — the exact opposite of "skip this one, keep the
   * series". A skipped reminder is as active as a fired one.
   *
   * So the active set is everything EXCEPT `dismissed`, and `dismissed` is
   * the only terminal state.
   */
  async listActive(): Promise<Reminder[]> {
    const all = await this.list();
    return all.filter((r) => r.state !== 'dismissed');
  }

  /** The reminder attached to one date, if there is one. */
  async findFor(parentNodeId: string, datePropertyId?: string | null): Promise<Reminder | null> {
    const all = await this.listActive();
    return (
      all.find(
        (r) =>
          r.parent_node_id === parentNodeId &&
          (datePropertyId == null || r.date_property_id === datePropertyId),
      ) ?? null
    );
  }

  /**
   * Skip ONE occurrence: advance `next_fire_at` past the next firing.
   *
   * "Skip this one" is NOT "stop repeating" — the series continues, and the
   * state becomes `skipped` so the UI can say so. The next firing is
   * computed from the CURRENT `next_fire_at`, so the skipped slot is the
   * one that was about to happen.
   */
  async skipNext(id: string): Promise<Reminder> {
    const r = await this.load(id);
    if (!r) throw new Error(`ReminderStore.skipNext: no reminder ${id}`);
    const from = new Date(r.next_fire_at);
    const next = computeNextFireAt(from, r.repeat, {
      repeatOn: r.repeat_on ?? null,
      repeatTime: r.repeat_time ?? null,
    });
    return this.save({ ...r, next_fire_at: next.toISOString(), state: 'skipped' });
  }

  /**
   * Stop repeating: dismiss the reminder outright.
   *
   * A dismissed reminder stays in the graph — it is a record of what the
   * operator asked for and then cancelled — but the scheduler will never
   * pick it up, because the scheduler only acts on `state === 'scheduled'`.
   */
  async dismiss(id: string): Promise<Reminder> {
    const r = await this.load(id);
    if (!r) throw new Error(`ReminderStore.dismiss: no reminder ${id}`);
    return this.save({ ...r, state: 'dismissed' });
  }

  /** Delete a reminder outright. */
  async delete(id: string): Promise<void> {
    await this.adapter.delete(`${REMINDER_PREFIX}${id}`);
    this.bus.emit('reminder:deleted', { type: 'reminder:deleted', reminderId: id });
  }

  // ── notifications ──────────────────────────────────────────────────

  /**
   * Read the notifications FROM THE GRAPH, not from the cache.
   *
   * MEASURED on production, and this is the last seam. The scheduler runs
   * on the server and writes `freshcards:notification:<id>` into the
   * graph. The browser's `remoteAdapter.list()` reads the IN-MEMORY CACHE
   * and the localStorage MIRROR — and the cache is only populated by
   * `RemoteState.init()`, once, at boot. So a notification the scheduler
   * created while the tab was open was INVISIBLE to the browser: the bell
   * showed no badge and the inbox was empty, while the notification sat in
   * the graph.
   *
   * Measured on the live site at that moment:
   *
   *   graph:  freshcards:notification:n-127a87fb-…  read=false
   *   browser: badge=null, inbox count=0
   *
   * ⇒ The notification feed reads the SERVER. The cache is still the
   * authority for what this browser has WRITTEN (the reminder), and the
   * server is the authority for what the scheduler has DONE.
   *
   * And it degrades: no token, no server, or a failed read means the
   * browser falls back to the cache, so a static deploy still shows the
   * notifications it knows about.
   */
  async listNotificationsFromGraph(): Promise<Notification[]> {
    if (graphAuthProblem()) return this.listNotificationsFromCache();
    try {
      const res = await graphFetch('/api/fcc', `/kv?prefix=${encodeURIComponent(NOTIFICATION_PREFIX)}`);
      if (!res || !res.ok) return this.listNotificationsFromCache();
      const body = await res.json();
      const values = body?.values;
      if (!values || typeof values !== 'object') return this.listNotificationsFromCache();
      const out: Notification[] = [];
      for (const [k, raw] of Object.entries(values as Record<string, unknown>)) {
        if (typeof raw !== 'string') continue;
        try {
          const node = JSON.parse(raw);
          if (isNotification(node)) out.push(node);
        } catch {
          // A malformed node is skipped, not fatal.
        }
      }
      out.sort((a, b) => (b.fired_at || '').localeCompare(a.fired_at || ''));
      return out;
    } catch {
      return this.listNotificationsFromCache();
    }
  }

  /**
   * The notifications this browser knows about, from the cache and the
   * mirror. The FALLBACK for every path that cannot reach the server.
   */
  private async listNotificationsFromCache(): Promise<Notification[]> {
    const keys = await this.adapter.list(NOTIFICATION_PREFIX);
    const out: Notification[] = [];
    for (const k of keys) {
      const raw = await this.adapter.load(k);
      if (isNotification(raw)) out.push(raw);
    }
    out.sort((a, b) => (b.fired_at || '').localeCompare(a.fired_at || ''));
    return out;
  }

  /**
   * List the notifications. ONLY THE SCHEDULER CREATES THEM — this is a
   * read side, and there is deliberately no `create` here.
   *
   * The SERVER is the authority: the scheduler writes notifications on the
   * VPS, and the browser's cache cannot see them. So this reads the graph
   * and falls back to the cache.
   */
  async listNotifications(): Promise<Notification[]> {
    const fromGraph = await this.listNotificationsFromGraph();
    if (fromGraph.length) return fromGraph;
    const keys = await this.adapter.list(NOTIFICATION_PREFIX);
    const out: Notification[] = [];
    for (const k of keys) {
      const raw = await this.adapter.load(k);
      if (isNotification(raw)) out.push(raw);
    }
    out.sort((a, b) => (b.fired_at || '').localeCompare(a.fired_at || ''));
    return out;
  }

  /** The unread notifications — what the bell counts. */
  async listUnread(): Promise<Notification[]> {
    const all = await this.listNotifications();
    return all.filter((n) => n.read === false);
  }

  /** Mark one notification read. The bell count falls by one. */
  /**
   * Mark one notification read. The bell count falls by one.
   *
   * MEASURED: this used to look the notification up in the CACHE, but the
   * notification came from the SERVER (the scheduler wrote it), so the
   * lookup found nothing and the click did nothing. And a `read` flag set
   * only in the cache would be lost on the next read, which reads the
   * server.
   *
   * So: find the node in the GRAPH, write the updated node back to the
   * GRAPH, and mirror it locally. The server is where the read flag has
   * to live, because the server is where the notification lives.
   */
  async markRead(id: string): Promise<Notification | null> {
    const key = `${NOTIFICATION_PREFIX}${id}`;
    let raw: unknown = null;

    // The graph first — that is where the notification actually is.
    if (!graphAuthProblem()) {
      try {
        const res = await graphFetch('/api/fcc', `/kv?prefix=${encodeURIComponent(key)}`);
        if (res && res.ok) {
          const body = await res.json();
          const v = body?.values?.[key];
          if (typeof v === 'string') raw = JSON.parse(v);
        }
      } catch {
        // Fall through to the cache.
      }
    }
    // The cache, for a browser that cannot reach the graph.
    if (raw === null) raw = await this.adapter.load(key);
    if (!isNotification(raw)) return null;

    const updated: Notification = { ...raw, read: true, updatedAt: new Date().toISOString() };
    await this.adapter.save(key, updated);
    // AND to the server, so the read flag survives a reload and a reload
    // on another device. The cache write above is the local mirror.
    if (!graphAuthProblem()) {
      try {
        await graphFetch('/api/fcc', '/kv', {
          method: 'PUT',
          body: JSON.stringify({ values: { [key]: JSON.stringify(updated) } }),
        });
      } catch {
        // A failed server write is not fatal: the local mirror holds it.
      }
    }
    this.bus.emit('notification:read', { type: 'notification:read', notification: updated });
    return updated;
  }
}

export { REMINDER_PREFIX, NOTIFICATION_PREFIX };
export type { Reminder, Notification, ReminderRepeat, ReminderState };
